/**
 * Count Slice
 * Manages manual count entries and aggregated count views.
 * Two separate buckets: manual and beacon (LAT-27).
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as countApi from '../../services/countApi';

const initialState = {
  countsByProject: {},
  entriesByProject: {},
  loading: false,
  error: null,
  // ASS-64: entry load failures, kept apart from count errors so a failed
  // /counts fixture call can't mask (or be mistaken for) a failed entries load.
  entriesError: null,
};

// The backend returns { error: '...' } on 4xx/5xx; ApiError.message is only
// "HTTP 400". Prefer the server's message so the user sees why.
export const entryErrorMessage = (error, fallback) => {
  if (error?.status === 0) return 'Can\'t reach the server. Check your connection and try again.';
  if (error?.status === 401) return 'Your session has expired. Sign in again.';
  if (error?.status === 403) return 'You don\'t have access to this project.';
  return error?.data?.error || error?.data?.message || fallback;
};

// Async thunks
export const fetchCountsByProject = createAsyncThunk(
  'count/fetchByProject',
  async (projectId, { rejectWithValue }) => {
    try {
      const result = await countApi.fetchProjectCounts(projectId);
      return { projectId, items: result.items || [] };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCountsByViewport = createAsyncThunk(
  'count/fetchByViewport',
  async ({ projectId, bounds }, { rejectWithValue }) => {
    try {
      const result = await countApi.fetchViewportCounts(projectId, bounds);
      return { projectId, items: result.items || [] };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createEntryAction = createAsyncThunk(
  'count/createEntry',
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const result = await countApi.submitEntry(projectId, data);
      return { projectId, item: result.item, duplicate: result.duplicate };
    } catch (error) {
      return rejectWithValue(entryErrorMessage(error, 'Could not save this entry. Try again.'));
    }
  }
);

export const fetchEntries = createAsyncThunk(
  'count/fetchEntries',
  async ({ projectId, filters = {} }, { rejectWithValue }) => {
    try {
      // Every page, not just the first 50: totals (inventory panel, AST-88
      // locations, AST-89 badges) are sums over the whole list, so a partial
      // list silently undercounts. 200/page; capped so a runaway can't spin.
      const items = [];
      let nextToken;
      let pages = 0;
      do {
        const result = await countApi.fetchEntries(projectId, { limit: 200, ...filters, ...(nextToken ? { nextToken } : {}) });
        items.push(...(result.items || []));
        nextToken = result.nextToken;
        pages++;
      } while (nextToken && pages < 50);
      return { projectId, items, nextToken: nextToken || null };
    } catch (error) {
      return rejectWithValue(entryErrorMessage(error, 'Could not load entries. Try again.'));
    }
  }
);

const countSlice = createSlice({
  name: 'count',
  initialState,
  reducers: {
    clearCountsForProject: (state, action) => {
      const projectId = action.payload;
      delete state.countsByProject[projectId];
    },
    clearEntriesForProject: (state, action) => {
      const projectId = action.payload;
      delete state.entriesByProject[projectId];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch counts by project
      .addCase(fetchCountsByProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCountsByProject.fulfilled, (state, action) => {
        state.loading = false;
        state.countsByProject[action.payload.projectId] = action.payload.items;
      })
      .addCase(fetchCountsByProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch counts by viewport
      .addCase(fetchCountsByViewport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCountsByViewport.fulfilled, (state, action) => {
        state.loading = false;
        state.countsByProject[action.payload.projectId] = action.payload.items;
      })
      .addCase(fetchCountsByViewport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create entry
      .addCase(createEntryAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEntryAction.fulfilled, (state, action) => {
        state.loading = false;
        const { projectId, item } = action.payload;
        if (!state.entriesByProject[projectId]) {
          state.entriesByProject[projectId] = [];
        }
        // Prepend new entry (newest first)
        state.entriesByProject[projectId] = [item, ...state.entriesByProject[projectId]];
      })
      .addCase(createEntryAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch entries
      .addCase(fetchEntries.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.entriesError = null;
      })
      .addCase(fetchEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.entriesByProject[action.payload.projectId] = action.payload.items;
      })
      .addCase(fetchEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.entriesError = action.payload;
      });
  },
});

export const {
  clearCountsForProject,
  clearEntriesForProject,
  clearError,
} = countSlice.actions;

export default countSlice.reducer;
